import { SvelteMap } from 'svelte/reactivity'

import {
    ApiResponseError,
    type ApiClient,
    type MessagingMessagesBefore,
    type MessagingSendMessageResult
} from '@tadpoles/api-client'
import {
    InstantMessageRoles,
    InstantMessageTypes,
    isRecord,
    toShortIsoLocalDate,
    type DailyMessages,
    type InstantMessage,
    type MessagingMetadata
} from '@tadpoles/common'

import type { Connectivity, ConnectivityObserver } from '../connectivity/service.js'
import type { FamilyUserLocalBacking } from '../persistence/family-local-backing.js'
import type { MessagingStore } from '../persistence/stores/messaging-store.js'
import type { PushNotificationEvent } from '../push-notifications/events.js'
import type { PushNotificationsService } from '../push-notifications/service.js'
import type { ServiceLifecycle } from '../service-lifecycle.js'
import { UserScopedService, type UserScopedServiceScope } from '../user-scoped-service-scope.js'
import { BookmarkSynchronizer } from './bookmark-synchronizer.js'
import { MessagingConversation } from './conversation.svelte.js'
import type {
    MessagingConversationProjection,
    MessagingSendInput,
    MessagingSendOperation
} from './state.js'

const MessageCacheRetentionDays = 30
const MultipartProcessingErrorCode = 'multipart_processing'

export type MessagingServiceOptions = {
    connectivity?: Connectivity
    createId?: () => string
    now?: () => number
    pushNotifications?: Pick<PushNotificationsService, 'subscribe'>
}

export class MessagingService
    extends UserScopedService
    implements ConnectivityObserver, ServiceLifecycle
{
    readonly #apiClient: Pick<ApiClient, 'messaging' | 'multipart'>
    readonly #connectivity: Connectivity | undefined
    readonly #createId: () => string
    readonly #historyRequests = new Map<string, Promise<void>>()
    readonly #localBacking: FamilyUserLocalBacking
    readonly #now: () => number
    readonly #pushNotifications: Pick<PushNotificationsService, 'subscribe'> | undefined
    readonly #sendConfirmations = new Map<
        string,
        Map<string, PromiseWithResolvers<boolean>>
    >()
    #activeSubjectKey = $state<string>()
    readonly #conversations = new SvelteMap<string, MessagingConversation>()
    #sortedConversations = $derived(
        [...this.#conversations.values()]
            .map((conversation) => conversation.view)
            .sort((left, right) => left.subjectKey.localeCompare(right.subjectKey))
    )
    #destroyed = false
    readonly #bookmarks: BookmarkSynchronizer
    #operationController = new AbortController()
    #store: MessagingStore | undefined
    #unreadConversationCount = $derived(
        this.#sortedConversations.filter(({ metadata }) => this.#isUnread(metadata)).length
    )
    #unregisterConnectivity: (() => void) | undefined
    #unsubscribePush: (() => void) | undefined

    public constructor(
        scope: UserScopedServiceScope,
        localBacking: FamilyUserLocalBacking,
        apiClient: Pick<ApiClient, 'messaging' | 'multipart'>,
        options: MessagingServiceOptions = {}
    ) {
        super(scope)
        this.#apiClient = apiClient
        this.#connectivity = options.connectivity
        this.#createId = options.createId ?? (() => crypto.randomUUID())
        this.#localBacking = localBacking
        this.#now = options.now ?? Date.now
        this.#pushNotifications = options.pushNotifications
        this.#bookmarks = new BookmarkSynchronizer(apiClient.messaging, {
            eligibleSubjectKeys: () => this.#eligibleSubjectKeys(),
            getMetadata: (subjectKey) => this.#conversations.get(subjectKey)?.metadata,
            setMetadata: (subjectKey, metadata) =>
                this.#ensureConversation(subjectKey).setMetadata(metadata),
            onChange: () => {
                this.#removeIneligibleConversations(this.#eligibleSubjectKeys())
                this.#scheduleCacheWrite()
            }
        })
    }

    public get activeConversation(): MessagingConversationProjection | undefined {
        return this.#activeSubjectKey === undefined
            ? undefined
            : this.getConversation(this.#activeSubjectKey)
    }

    public get conversations(): readonly MessagingConversationProjection[] {
        return this.#sortedConversations
    }

    public get unreadConversationCount(): number {
        return this.#unreadConversationCount
    }

    public async initialize(): Promise<void> {
        this.#assertAvailable()

        try {
            this.#store = await this.#localBacking.getMessagingStore()
            if (this.#store) {
                const snapshot = await this.#store.load(this.#cacheCutoffDate())
                this.#restoreSnapshot(snapshot.dailyMessages, snapshot.metadata)
                if (snapshot.omittedRecordCount > 0) {
                    console.warn(
                        `Omitted ${snapshot.omittedRecordCount} invalid or expired messaging cache records.`
                    )
                }
            }
        } catch (error) {
            console.warn('Unable to restore messaging cache.', error)
            this.#store = undefined
        }

        this.#unregisterConnectivity = this.#connectivity?.register(this)
        this.#unsubscribePush = this.#pushNotifications?.subscribe(this.#handlePush)
    }

    public async resume(): Promise<void> {
        this.#assertAvailable()
        await this.#refreshForEvent()
    }

    public suspend(): void {}

    public destroy(): void {
        if (this.#destroyed) {
            return
        }

        this.#destroyed = true
        this.#operationController.abort()
        this.#unregisterConnectivity?.()
        this.#unsubscribePush?.()
        this.#unregisterConnectivity = undefined
        this.#unsubscribePush = undefined
        this.#historyRequests.clear()
        this.#bookmarks.destroy()
        this.#store = undefined
        this.#activeSubjectKey = undefined
        for (const conversation of this.#conversations.values()) {
            conversation.clear()
        }
        this.#conversations.clear()
        this.#settleAllSendConfirmations(false)
    }

    public async setOnline(online: boolean): Promise<void> {
        if (!online || this.#destroyed) {
            return
        }

        await this.#refreshForEvent()
    }

    public getConversation(subjectKey: string): MessagingConversationProjection | undefined {
        return this.#conversations.get(subjectKey)?.view
    }

    public async openConversation(subjectKey: string): Promise<void> {
        this.#assertAvailable()
        this.#assertEligibleSubject(subjectKey)
        this.#activeSubjectKey = subjectKey
        this.#ensureConversation(subjectKey)
        await this.loadRecent(subjectKey)
    }

    public async refreshMetadata(): Promise<void> {
        this.#assertAvailable()
        await this.#bookmarks.refreshMetadata()
    }

    public async loadRecent(subjectKey: string): Promise<void> {
        this.#assertAvailable()
        this.#assertEligibleSubject(subjectKey)
        await Promise.all([this.refreshMetadata(), this.#loadHistory(subjectKey)])
    }

    public async loadOlder(subjectKey: string): Promise<void> {
        this.#assertAvailable()
        this.#assertEligibleSubject(subjectKey)
        const conversation = this.#ensureConversation(subjectKey)
        const previousMessage = conversation.view.messages[0]
        let nextBefore = conversation.oldestDate
        do {
            const requestedBefore = nextBefore
            await this.#loadHistory(subjectKey, requestedBefore)
            const nextMessage = conversation.view.messages[0]
            const advancedBefore = conversation.oldestDate
            if (
                nextMessage?.id !== previousMessage?.id ||
                nextMessage?.captureTimestamp !== previousMessage?.captureTimestamp ||
                advancedBefore === requestedBefore
            ) {
                return
            }
            nextBefore = advancedBefore
        } while (nextBefore !== undefined)
    }

    public async bookmark(subjectKey: string, timestamp: number): Promise<boolean> {
        this.#assertAvailable()
        this.#assertEligibleSubject(subjectKey)
        return await this.#bookmarks.advance(subjectKey, timestamp)
    }

    public sendMessage(input: MessagingSendInput): MessagingSendOperation {
        this.#assertAvailable()
        this.#assertEligibleSubject(input.subjectKey)
        const text = input.text?.trim()
        const attachments = [...(input.attachments ?? [])]

        if (!text && attachments.length === 0) {
            throw new Error('A message requires text or at least one attachment.')
        }

        const timestamp = this.#now()
        const message: InstantMessage = {
            id: this.#createId(),
            clientUpdateTimestamp: timestamp,
            captureTimestamp: timestamp,
            deleted: false,
            authorKey: this.userContext.person.key,
            authorName: this.userContext.person.displayName,
            role: InstantMessageRoles.parent,
            text,
            attachments,
            type: InstantMessageTypes.text
        }
        const conversation = this.#ensureConversation(input.subjectKey)
        conversation.addOutgoing(message)

        return this.#createSendOperation(input.subjectKey, message)
    }

    public retryMessage(subjectKey: string, messageId: string): MessagingSendOperation {
        this.#assertAvailable()
        this.#assertEligibleSubject(subjectKey)
        const conversation = this.#ensureConversation(subjectKey)
        const message = conversation.retrySend(messageId)

        return this.#createSendOperation(subjectKey, message)
    }

    public reconcileEligibility(): void {
        this.#assertAvailable()
        const eligibleSubjectKeys = this.#eligibleSubjectKeys()
        const removedConversation = this.#removeIneligibleConversations(eligibleSubjectKeys)

        if (this.#activeSubjectKey && !eligibleSubjectKeys.has(this.#activeSubjectKey)) {
            this.#activeSubjectKey = undefined
        }

        this.#bookmarks.retainEligible(eligibleSubjectKeys)

        if (removedConversation) {
            this.#scheduleCacheWrite()
        }
    }

    async #loadHistory(subjectKey: string, before?: MessagingMessagesBefore): Promise<void> {
        const requestKey = `${subjectKey}:${before instanceof Date ? before.toISOString() : (before ?? 'recent')}`
        const existingRequest = this.#historyRequests.get(requestKey)
        if (existingRequest) {
            return await existingRequest
        }

        const request = this.#fetchAndMergeHistory(subjectKey, before)
        this.#historyRequests.set(requestKey, request)

        try {
            await request
        } finally {
            if (this.#historyRequests.get(requestKey) === request) {
                this.#historyRequests.delete(requestKey)
            }
        }
    }

    async #fetchAndMergeHistory(
        subjectKey: string,
        before?: MessagingMessagesBefore
    ): Promise<void> {
        const payload = await this.#apiClient.messaging.getMessages(subjectKey, before, {
            signal: this.#operationController.signal
        })
        this.#assertAvailable()
        if (!this.#eligibleSubjectKeys().has(subjectKey)) {
            return
        }
        const dailyMessages = Object.values(payload.messages)
        const conversation = this.#ensureConversation(subjectKey)
        conversation.mergeHistory(dailyMessages)
        this.#settleCanonicalSendConfirmations(subjectKey, conversation)
        this.#scheduleCacheWrite()
    }

    async #performSend(subjectKey: string, message: InstantMessage): Promise<void> {
        try {
            const result = await this.#apiClient.messaging.sendMessage(
                {
                    subjectKey,
                    message,
                    knownRevision: this.#conversations
                        .get(subjectKey)
                        ?.knownRevision(message.captureTimestamp)
                },
                { signal: this.#operationController.signal }
            )
            this.#assertAvailable()
            if (!this.#eligibleSubjectKeys().has(subjectKey)) {
                this.#settleSendConfirmation(subjectKey, message.id, false)
                return
            }
            await this.#handleSendResult(subjectKey, message, result)
        } catch (error) {
            if (this.#operationController.signal.aborted) {
                return
            }
            if (!this.#eligibleSubjectKeys().has(subjectKey)) {
                this.#settleSendConfirmation(subjectKey, message.id, false)
                return
            }

            if (error instanceof ApiResponseError) {
                this.#ensureConversation(subjectKey).markFailed(
                    message.id,
                    error.error?.message ?? 'Message could not be sent.'
                )
                this.#settleSendConfirmation(subjectKey, message.id, false)
                return
            }

            console.warn('Messaging send completion is uncertain; reconciling history.', error)
            await this.#reconcileUncertainSend(subjectKey)
        }
    }

    async #handleSendResult(
        subjectKey: string,
        message: InstantMessage,
        result: MessagingSendMessageResult
    ): Promise<void> {
        if (result.payload) {
            if (
                result.payload.subjectKey !== subjectKey ||
                result.payload.message.id !== message.id
            ) {
                console.warn('Messaging send response identity did not match the submitted message.')
                await this.#reconcileUncertainSend(subjectKey)
                return
            }

            const conversation = this.#ensureConversation(subjectKey)
            conversation.confirmSend(result.payload)
            this.#settleCanonicalSendConfirmations(subjectKey, conversation)
            this.#scheduleCacheWrite()
            return
        }

        if (result.errorCode !== MultipartProcessingErrorCode || !result.manifestId) {
            this.#ensureConversation(subjectKey).markFailed(
                message.id,
                result.errorMessage ?? 'Message could not be sent.'
            )
            this.#settleSendConfirmation(subjectKey, message.id, false)
            return
        }

        try {
            await result.upload?.completion
            await this.#apiClient.multipart.getStatus(result.manifestId, {
                signal: this.#operationController.signal
            })
        } catch (error) {
            if (this.#operationController.signal.aborted) {
                return
            }

            console.warn('Multipart messaging completion remains uncertain.', error)
        }

        await this.#reconcileUncertainSend(subjectKey)
    }

    async #reconcileUncertainSend(subjectKey: string): Promise<void> {
        if (!this.#eligibleSubjectKeys().has(subjectKey)) {
            return
        }

        try {
            await this.#loadHistory(subjectKey)
        } catch (error) {
            if (!this.#operationController.signal.aborted) {
                console.warn('Unable to reconcile an uncertain messaging send.', error)
            }
            return
        }
    }

    async #refreshForEvent(): Promise<void> {
        const operations: Promise<unknown>[] = [this.refreshMetadata()]
        if (this.#activeSubjectKey) {
            operations.push(this.#loadHistory(this.#activeSubjectKey))
        }

        await Promise.allSettled(operations)
    }

    #handlePush = (event: PushNotificationEvent): void => {
        if (this.#destroyed) {
            return
        }

        const subjectKey = this.#readPushSubjectKey(event.payload)
        if (!subjectKey || !this.#eligibleSubjectKeys().has(subjectKey)) {
            return
        }

        void Promise.all([this.refreshMetadata(), this.#loadHistory(subjectKey)]).catch((error) => {
            if (!this.#operationController.signal.aborted) {
                console.warn('Unable to refresh messaging after push.', error)
            }
        })
    }

    #readPushSubjectKey(payload: unknown): string | undefined {
        if (!isRecord(payload)) {
            return undefined
        }

        const data = isRecord(payload.data) ? payload.data : payload
        for (const key of ['k', 'subjectKey', 'subject_key']) {
            const value = data[key]
            if (typeof value === 'string' && value) {
                return value
            }
        }

        return undefined
    }

    #restoreSnapshot(
        dailyMessages: readonly DailyMessages[],
        metadata: readonly MessagingMetadata[]
    ): void {
        const eligibleSubjectKeys = this.#eligibleSubjectKeys()
        const dailyBySubject = new Map<string, DailyMessages[]>()
        for (const daily of dailyMessages) {
            if (!eligibleSubjectKeys.has(daily.subjectKey)) {
                continue
            }
            const subjectDailyMessages = dailyBySubject.get(daily.subjectKey) ?? []
            subjectDailyMessages.push(daily)
            dailyBySubject.set(daily.subjectKey, subjectDailyMessages)
        }
        const metadataBySubject = new Map(
            metadata
                .filter(({ subjectKey }) => eligibleSubjectKeys.has(subjectKey))
                .map((value) => [value.subjectKey, value])
        )

        for (const subjectKey of eligibleSubjectKeys) {
            this.#ensureConversation(subjectKey).restore(
                dailyBySubject.get(subjectKey) ?? [],
                metadataBySubject.get(subjectKey)
            )
        }
    }

    #removeIneligibleConversations(eligibleSubjectKeys: ReadonlySet<string>): boolean {
        let removedConversation = false
        for (const [subjectKey, conversation] of this.#conversations) {
            if (!eligibleSubjectKeys.has(subjectKey)) {
                conversation.clear()
                this.#conversations.delete(subjectKey)
                this.#settleSubjectSendConfirmations(subjectKey, false)
                removedConversation = true
            }
        }

        return removedConversation
    }

    #ensureConversation(subjectKey: string): MessagingConversation {
        let conversation = this.#conversations.get(subjectKey)
        if (!conversation) {
            conversation = new MessagingConversation(subjectKey)
            this.#conversations.set(subjectKey, conversation)
        }
        return conversation
    }

    #createSendOperation(
        subjectKey: string,
        message: InstantMessage
    ): MessagingSendOperation {
        const confirmation = Promise.withResolvers<boolean>()
        const subjectConfirmations = this.#sendConfirmations.get(subjectKey) ?? new Map()
        subjectConfirmations.set(message.id, confirmation)
        this.#sendConfirmations.set(subjectKey, subjectConfirmations)
        return {
            confirmation: confirmation.promise,
            completion: this.#performSend(subjectKey, message),
            messageId: message.id
        }
    }

    #settleSendConfirmation(subjectKey: string, messageId: string, confirmed: boolean): void {
        const subjectConfirmations = this.#sendConfirmations.get(subjectKey)
        const confirmation = subjectConfirmations?.get(messageId)
        if (!confirmation) {
            return
        }

        confirmation.resolve(confirmed)
        subjectConfirmations?.delete(messageId)
        if (subjectConfirmations?.size === 0) {
            this.#sendConfirmations.delete(subjectKey)
        }
    }

    #settleCanonicalSendConfirmations(
        subjectKey: string,
        conversation: MessagingConversation
    ): void {
        const subjectConfirmations = this.#sendConfirmations.get(subjectKey)
        if (!subjectConfirmations) {
            return
        }

        const canonicalMessageIds = new Set(conversation.view.messages.map(({ id }) => id))
        for (const messageId of subjectConfirmations.keys()) {
            if (canonicalMessageIds.has(messageId)) {
                this.#settleSendConfirmation(subjectKey, messageId, true)
            }
        }
    }

    #settleSubjectSendConfirmations(subjectKey: string, confirmed: boolean): void {
        const subjectConfirmations = this.#sendConfirmations.get(subjectKey)
        if (!subjectConfirmations) {
            return
        }

        for (const confirmation of subjectConfirmations.values()) {
            confirmation.resolve(confirmed)
        }
        this.#sendConfirmations.delete(subjectKey)
    }

    #settleAllSendConfirmations(confirmed: boolean): void {
        for (const subjectKey of this.#sendConfirmations.keys()) {
            this.#settleSubjectSendConfirmations(subjectKey, confirmed)
        }
    }

    #scheduleCacheWrite(): void {
        const store = this.#store
        if (!store) {
            return
        }

        const snapshots = [...this.#conversations.values()]
            .sort((left, right) => left.subjectKey.localeCompare(right.subjectKey))
            .map((conversation) => conversation.snapshot())
        const dailyMessages = snapshots.flatMap((snapshot) => [...snapshot.dailyMessages])
        const metadata = snapshots.flatMap((snapshot) =>
            snapshot.metadata ? [snapshot.metadata] : []
        )
        const cutoffDate = this.#cacheCutoffDate()
        void this.enqueueCacheWrite(async () => {
            await store.replace(dailyMessages, metadata, cutoffDate)
        }).catch((error) => {
            if (!this.#operationController.signal.aborted) {
                console.warn('Unable to persist messaging state.', error)
            }
        })
    }

    #cacheCutoffDate(): string {
        const cutoff = new Date(this.#now())
        cutoff.setDate(cutoff.getDate() - MessageCacheRetentionDays)
        return toShortIsoLocalDate(cutoff)
    }

    #eligibleSubjectKeys(): Set<string> {
        const { child, feature } = this.userServices
        return new Set(
            child.children
                .filter(({ key }) => feature.getForChild(key).enable_family_messaging)
                .map(({ key }) => key)
        )
    }

    #assertEligibleSubject(subjectKey: string): void {
        if (!this.#eligibleSubjectKeys().has(subjectKey)) {
            throw new Error('Messaging requires an accessible messaging-enabled child.')
        }
    }

    #isUnread(metadata: MessagingMetadata | undefined): boolean {
        if (!metadata?.lastUnreadMessage) {
            return false
        }

        return (
            metadata.bookmarkTimestamp === undefined ||
            metadata.lastUnreadMessage.captureTimestamp > metadata.bookmarkTimestamp
        )
    }

    #assertAvailable(): void {
        if (this.#destroyed) {
            throw new Error('Messaging service has been destroyed.')
        }
    }
}
